import { z } from 'zod';

const mitigationBandSchema = z.object({
  lowerBound: z.number(),
  upperBound: z.number(),
});

const dimensionSchema = z.object({
  aggregateAvg: z.number().nullable(),
  multiplier: z.number(),
  band: mitigationBandSchema.nullable(),
});

const controlTypeContributionSchema = z.object({
  type: z.string().nullable(),
  count: z.number(),
  avgEffectiveness: z.number(),
  likelihoodWeight: z.number(),
  impactWeight: z.number(),
});

export const RiskScoreBreakdownIncompleteReason = {
  MissingInherentAssessment: 'missing_inherent_assessment',
  NoControlsLinked: 'no_controls_linked',
  AllControlsFilteredOut: 'all_controls_filtered_out',
  ControlsWithoutTestResults: 'controls_without_test_results',
} as const;

export type RiskScoreBreakdownIncompleteReason =
  (typeof RiskScoreBreakdownIncompleteReason)[keyof typeof RiskScoreBreakdownIncompleteReason];

const incompleteSchema = z.object({
  status: z.literal('incomplete'),
  reason: z.enum([
    RiskScoreBreakdownIncompleteReason.MissingInherentAssessment,
    RiskScoreBreakdownIncompleteReason.NoControlsLinked,
    RiskScoreBreakdownIncompleteReason.AllControlsFilteredOut,
    RiskScoreBreakdownIncompleteReason.ControlsWithoutTestResults,
  ]),
  details: z.string().optional(),
});

export const roundingModeSchema = z.enum(['nearest', 'up', 'down']);

export type RoundingMode = z.infer<typeof roundingModeSchema>;

const completeSchema = z.object({
  status: z.literal('complete'),
  inherent: z.object({
    likelihood: z.number(),
    impact: z.number(),
    score: z.number(),
  }),
  residual: z.object({
    likelihood: z.number(),
    impact: z.number(),
    score: z.number(),
  }),
  controlTypes: z.array(controlTypeContributionSchema),
  likelihood: dimensionSchema,
  impact: dimensionSchema,
  /**
   * Rounding mode applied to residual likelihood/impact (RSP-5817).
   * Optional for backward compatibility — existing persisted breakdowns
   * without this field still parse. Always populated for new breakdowns
   * emitted by models that read a `roundingMode` config.
   */
  roundingMode: roundingModeSchema.optional(),
});

export const riskScoreBreakdownSchema = z.discriminatedUnion('status', [
  completeSchema,
  incompleteSchema,
]);

export type RiskScoreBreakdown = z.infer<typeof riskScoreBreakdownSchema>;

export type RiskScoreBreakdownComplete = z.infer<typeof completeSchema>;
export type RiskScoreBreakdownIncomplete = z.infer<typeof incompleteSchema>;
export type RiskScoreBreakdownControlTypeContribution = z.infer<
  typeof controlTypeContributionSchema
>;
